import React from "react";
import {
  CardActions,
  CardContent,
  Grid,
  Typography,
  Divider,
} from "@material-ui/core";
import { styled, makeStyles } from "@material-ui/core/styles";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { useFirebase } from "../Firebase";
import { useError } from "../ErrorProvider";
import { CompanyCodeDisplay } from "../StyledComponents";
import { brand } from "../StyledComponents/theme";
import { PageHeader, AppCard, StatCard, StatSkeleton } from "../UI";
import { ErrorOutline } from "@material-ui/icons";
import * as ROUTES from "../../constants/routes";
import CurrencyFormat from "react-currency-format";

const TypographyCentered = styled(Typography)({
  textAlign: "center",
});

const useStyles = makeStyles({
  participantPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    border: `1px solid ${brand.divider}`,
    boxShadow: brand.cardShadow,
    cursor: "pointer",
    transition: "box-shadow 200ms ease, transform 200ms ease",
    "&:hover": {
      boxShadow: brand.cardShadowHover,
      transform: "translateY(-1px)",
    },
  },
  participantIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    border: `1px solid ${brand.orange}`,
    backgroundColor: brand.orangeTint,
  },
  participantIconGlyph: {
    color: brand.orange,
  },
  storyFlow: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  connector: {
    width: "2px",
    height: "18px",
    backgroundColor: brand.border,
    margin: "0 0 0 68px",
    borderRadius: "2px",
  },
});

const currency = (value, decimals = 2) => (
  <CurrencyFormat
    value={value}
    displayType={"text"}
    thousandSeparator={true}
    prefix={"$"}
    decimalScale={decimals}
    fixedDecimalScale={true}
  />
);

const Stats = () => {
  const firebase = useFirebase();
  const { showError } = useError();
  const classes = useStyles();
  const history = useHistory();

  const { data, isLoading } = useQuery(
    "CompanyStatistics",
    firebase.getCompanyStatistics,
    {
      select: (data) => data?.data,
      onError: (err) => showError(err),
    }
  );

  const goToParticipants = () => history.push(ROUTES.HRDASHBOARDPARTICIPANTS);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        action={
          <CompanyCodeDisplay>
            Company Code: {data?.companyCode}
          </CompanyCodeDisplay>
        }
      />
      {isLoading ? (
        <StatSkeleton count={4} />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AppCard>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TypographyCentered variant="h4">
                      {data?.companyName}
                    </TypographyCentered>
                    {data?.tremendousAwards && (
                      <TypographyCentered variant="body2">
                        Automatic reward delivery enabled.
                      </TypographyCentered>
                    )}
                    {data?.newUserCount > 0 && (
                      <TypographyCentered>
                        <span
                          className={classes.participantPill}
                          role="button"
                          tabIndex={0}
                          onClick={goToParticipants}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              goToParticipants();
                            }
                          }}
                        >
                          <span className={classes.participantIcon}>
                            <ErrorOutline
                              className={classes.participantIconGlyph}
                              fontSize="small"
                            />
                          </span>
                          {data.newUserCount} New Participant(s)
                        </span>
                      </TypographyCentered>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.storyFlow}>
                      <StatCard
                        value={data?.participants}
                        caption="Employees Currently Building Their Emergency Savings"
                      />
                      <div className={classes.connector} />
                      <StatCard
                        value={currency(data?.weeklyInvestment)}
                        caption="Each Week Invested In Emergency Savings"
                      />
                      <div className={classes.connector} />
                      <StatCard
                        value={currency(data?.yearToDateSavings)}
                        caption="YTD Invested In Emergency Savings"
                      />
                      <div className={classes.connector} />
                      <StatCard
                        value={currency(data?.lifetimeSavings, 0)}
                        caption="Total Emergency Savings Since Program Start"
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
              <Divider orientation="horizontal" variant="fullWidth" />
              <CardActions>
                <Typography
                  component="div"
                  variant="body2"
                  style={{ flexGrow: 1, textAlign: "center" }}
                >
                  Questions? - Contact us at{" "}
                  <a href="mailto:support@smartsquirrel.me">
                    support@smartsquirrel.me
                  </a>
                </Typography>
              </CardActions>
            </AppCard>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default Stats;
